import { Component, inject, signal } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import {
  IGradeSystems,
  IGradeSystem,
  IScoreWeight,
  IScoreWeights,
} from '../../../../../interfaces/grade-system';
import { GradeSystemsService } from '../../../../../services/grade-systems';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FlashService } from '../../../../../services/flash';
import { Helpers } from '../../../../../utilities/helpers';
import { NgClass } from '@angular/common';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { Session } from '../../../../../services/session';

@Component({
  selector: 'app-grade-config',
  imports: [ReactiveFormsModule, NgClass, ActionButton, FormsModule, LoadingIndicator],
  templateUrl: './grade-config.html',
  styleUrl: './grade-config.css',
})
export class GradeConfig {
  constructor(
    private gradeSystemsService: GradeSystemsService,
    private session: Session,
  ) {
    this.getGradingSystems();
    this.getSoreWeights();

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;
  }

  protected gradingSystems = signal<IGradeSystems | null>(null);
  protected scoreWeights = signal<IScoreWeights | null>(null);
  protected isLoading = signal<boolean>(false);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly flashService = inject(FlashService);
  protected setGradeDetail = signal(false);
  protected addGradeConfig = signal<boolean>(false);
  protected addGradeSystem = signal<boolean>(false);
  protected editGradeSystem = signal<boolean>(false);
  protected configName!: string;
  protected classScore!: number;
  protected examScore!: number;
  protected isEditing = signal<boolean>(false);
  protected schoolId?: string;
  protected loggedUserRole?: string;
  protected readonly Helpers = Helpers;

  // Grade system creation
  protected gradeSystemName!: string;
  protected gradeSystemDescription!: string;
  protected gradeSystemStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED' = 'ACTIVE';
  protected gradeScales: Array<{ minimum_score: number | string; maximum_score: number | string; grade: string; remark: string; sequence_number: number }> = [
    { minimum_score: 80, maximum_score: 100, grade: 'A', remark: 'Excellent', sequence_number: 1 },
    { minimum_score: 70, maximum_score: 79.99, grade: 'B', remark: 'Very Good', sequence_number: 2 },
    { minimum_score: 60, maximum_score: 69.99, grade: 'C', remark: 'Good', sequence_number: 3 },
    { minimum_score: 50, maximum_score: 59.99, grade: 'D', remark: 'Pass', sequence_number: 4 },
    { minimum_score: 0, maximum_score: 49.99, grade: 'F', remark: 'Fail', sequence_number: 5 },
  ];

  // Grade system editing
  protected editGradeSystemName = '';
  protected editGradeSystemDescription = '';
  protected editGradeSystemStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED' = 'DRAFT';
  protected editGradeScales: Array<{ minimum_score: number | string; maximum_score: number | string; grade: string; remark: string; sequence_number: number }> = [];

  protected readonly gradeSystemStatuses: Array<'DRAFT' | 'ACTIVE' | 'ARCHIVED'> = [
    'DRAFT',
    'ACTIVE',
    'ARCHIVED',
  ];

  // Score weight editing
  protected editScoreWeight = signal<boolean>(false);
  protected selectedScoreWeight: IScoreWeights['payload'][number] | null = null;
  protected editScoreWeightName = '';
  protected editScoreWeightDescription = '';
  protected editScoreWeightClassScore!: number;
  protected editScoreWeightExamScore!: number;
  protected editScoreWeightStatus: 'DRAFT' | 'ACTIVE' | 'ARCHIVED' = 'ACTIVE';

  protected selectedGradeSystem: IGradeSystem['payload'] = {
    id: '',
    public_id: '',
    school_id: '',
    name: '',
    description: '',
    status: '',
    created_by: '',
    updated_by: '',
    created_at: '',
    updated_at: '',
    scales: [],
  };

  protected createGradeConfig(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error creating level. Try again.');
      return;
    }

    this.isEditing.set(true);

    const configForm = {
      school_id: this.schoolId,
      configuration_name: this.configName,
      class_score: this.classScore,
      examination_score: this.examScore,
    };

    this.gradeSystemsService.create(`${ApiEndpoints.url}/grade-config`, configForm).subscribe({
      next: () => {
        this.flashService.success('Score Weight created successfully');
        this.isEditing.set(false);
        this.addGradeConfig.set(false);
        this.getSoreWeights();
      },
      error: (err) => {
        this.isEditing.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not create Score Weight. Try again.',
        );
      },
    });
  }

  protected addScale(target: 'create' | 'edit' = 'create'): void {
    const scales = target === 'edit' ? this.editGradeScales : this.gradeScales;
    const nextSeq = scales.length + 1;

    scales.push({
      minimum_score: 0,
      maximum_score: 0,
      grade: '',
      remark: '',
      sequence_number: nextSeq,
    });
  }

  protected removeScale(index: number, target: 'create' | 'edit' = 'create'): void {
    const scales = target === 'edit' ? this.editGradeScales : this.gradeScales;

    if (scales.length > 1) {
      scales.splice(index, 1);
      // Reorder sequence numbers
      scales.forEach((scale, i) => {
        scale.sequence_number = i + 1;
      });
    }
  }

  protected openEditGradeSystem(): void {
    const system = this.selectedGradeSystem;

    this.editGradeSystemName = system.name ?? '';
    this.editGradeSystemDescription = system.description ?? '';
    this.editGradeSystemStatus = (system.status as 'DRAFT' | 'ACTIVE' | 'ARCHIVED') || 'DRAFT';
    this.editGradeScales = (system.scales ?? []).map((scale, index) => ({
      minimum_score: Number(scale.minimum_score),
      maximum_score: Number(scale.maximum_score),
      grade: scale.grade,
      remark: scale.remark ?? '',
      sequence_number: scale.sequence_number ?? index + 1,
    }));

    this.editGradeSystem.set(true);
  }

  protected updateGradeSystem(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const gradeSystemForm: any = {
      name: this.editGradeSystemName,
      description: this.editGradeSystemDescription || '',
      status: this.editGradeSystemStatus,
      scales: this.editGradeScales.map((scale) => ({
        minimum_score: Number(scale.minimum_score),
        maximum_score: Number(scale.maximum_score),
        grade: scale.grade,
        remark: scale.remark || null,
        sequence_number: Number(scale.sequence_number),
      })),
    };

    this.gradeSystemsService
      .editGradeSystem(
        `${ApiEndpoints.url}/grading-system/${this.selectedGradeSystem.id}`,
        gradeSystemForm,
      )
      .subscribe({
        next: (res) => {
          this.isEditing.set(false);
          this.editGradeSystem.set(false);

          if (res?.payload) {
            this.selectedGradeSystem = res.payload;
          }

          this.flashService.success('Grade system updated successfully');
          this.getGradingSystems();
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not update Grade System. Try again.',
          );
        },
      });
  }

  protected openEditScoreWeight(weight: IScoreWeights['payload'][number]): void {
    this.selectedScoreWeight = weight;
    this.editScoreWeightName = weight.configuration_name ?? '';
    this.editScoreWeightDescription = weight.description ?? '';
    this.editScoreWeightClassScore = Number(weight.class_score);
    this.editScoreWeightExamScore = Number(weight.examination_score);
    this.editScoreWeightStatus =
      (weight.status as 'DRAFT' | 'ACTIVE' | 'ARCHIVED') || 'ACTIVE';

    this.editScoreWeight.set(true);
  }

  protected updateScoreWeight(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.selectedScoreWeight) {
      this.flashService.failure('Select a score weight to update.');
      return;
    }

    const classScore = Number(this.editScoreWeightClassScore);
    const examinationScore = Number(this.editScoreWeightExamScore);

    if (classScore + examinationScore !== 100) {
      this.flashService.failure('Class score and examination score must add up to 100');
      return;
    }

    this.isEditing.set(true);

    const scoreWeightForm = {
      configuration_name: this.editScoreWeightName,
      description: this.editScoreWeightDescription || '',
      class_score: classScore,
      examination_score: examinationScore,
      status: this.editScoreWeightStatus,
    };

    this.gradeSystemsService
      .editScoreWeight(
        `${ApiEndpoints.url}/grade-config/${this.selectedScoreWeight.id}`,
        scoreWeightForm,
      )
      .subscribe({
        next: (res: IScoreWeight) => {
          this.isEditing.set(false);
          this.editScoreWeight.set(false);

          if (res?.payload) {
            this.selectedScoreWeight = res.payload;
          }

          this.flashService.success('Score weight updated successfully');
          this.getSoreWeights();
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not update Score Weight. Try again.',
          );
        },
      });
  }

  protected createGradeSystem(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId && this.loggedUserRole !== 'SUPER_ADMIN') {
      this.flashService.failure('Error creating grade system. Try again.');
      return;
    }

    this.isEditing.set(true);

    const gradeSystemForm: any = {
      name: this.gradeSystemName,
      description: this.gradeSystemDescription || '',
      status: this.gradeSystemStatus,
      scales: this.gradeScales.map((scale) => ({
        minimum_score: Number(scale.minimum_score),
        maximum_score: Number(scale.maximum_score),
        grade: scale.grade,
        remark: scale.remark || null,
        sequence_number: Number(scale.sequence_number),
      })),
    };

    if (this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      gradeSystemForm.school_id = this.schoolId;
    }

    this.gradeSystemsService.create(`${ApiEndpoints.url}/grading-system`, gradeSystemForm).subscribe({
      next: () => {
        this.flashService.success('Grade system created successfully');
        this.isEditing.set(false);
        this.addGradeSystem.set(false);
        this.getGradingSystems();
        // Reset form
        this.gradeSystemName = '';
        this.gradeSystemDescription = '';
        this.gradeSystemStatus = 'DRAFT';
        this.gradeScales = [
          { minimum_score: 80, maximum_score: 100, grade: 'A', remark: 'Excellent', sequence_number: 1 },
          { minimum_score: 70, maximum_score: 79.99, grade: 'B', remark: 'Very Good', sequence_number: 2 },
          { minimum_score: 60, maximum_score: 69.99, grade: 'C', remark: 'Good', sequence_number: 3 },
          { minimum_score: 50, maximum_score: 59.99, grade: 'D', remark: 'Pass', sequence_number: 4 },
          { minimum_score: 0, maximum_score: 49.99, grade: 'F', remark: 'Fail', sequence_number: 5 },
        ];
      },
      error: (err) => {
        this.isEditing.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not create Grade System. Try again.',
        );
      },
    });
  }

  protected selectGradeSystem(gradeSystem: IGradeSystem['payload']): void {
    this.selectedGradeSystem = gradeSystem;
    this.setGradeDetail.set(true);
  }

  protected getGradingSystems(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isLoading.set(true);
    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.gradeSystemsService.getGradeSystems(`${ApiEndpoints.url}/grading-system`).subscribe({
      next: (data: IGradeSystems) => {
        this.gradingSystems.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not load Grade Systems. Refresh the page',
        );
      },
    });
  }

  protected getSoreWeights(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isLoading.set(true);
    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.gradeSystemsService.getScoreWeights(`${ApiEndpoints.url}/grade-config`).subscribe({
      next: (data) => {
        this.scoreWeights.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not load Score Weights. Refresh the page',
        );
      },
    });
  }
}
